import { call, cancel, delay, fork, getContext, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  tranzilaHostedFieldsService,
  HostedFieldsEvent,
  HostedFieldsErrors,
} from 'utils/tranzilaHostedFieldsService';
import {
  pendingTranzilaHostedFieldsModalAction,
  setHostedFieldsFailModalAction,
  setHostedFieldsFailUploadModalAction,
  setHostedFieldsGoogleEnableAction,
  setHostedFieldsModalAction,
  setHostedFieldsSuccessModalAction,
  setHostedFieldsSuccessUploadModalAction,
  setPrivateBulletinMinimumPromotionPlanModal,
  setPrivateBulletinSelectPromotionPlanModal,
  setTranzilaHostedFieldsModalErrorsAction,
  setToastMessage,
  submittedPaymentViaHostedFieldsAction,
} from 'store/state/app/actions';
import {
  SET_HOSTED_FIELDS_FAIL_MODAL,
  SET_HOSTED_FIELDS_FAIL_UPLOAD_MODAL,
  SET_HOSTED_FIELDS_MODAL_STATE,
  SUBMITTED_PAYMENT_VIA_HOSTED_FIELDS,
  TRANZILA_HOSTED_FIELDS_PENDING_PAYMENT,
} from 'store/state/app/types';
import { hostedFieldsModalStateSelector, privateUserMonetizationEventsSourceSelector } from 'store/state/app/selectors';
import { userProfileSelector } from 'store/state/domainData/selectors';
import { userPaymentTransactionDataSelector, userPaymentTransactionsDataSelector } from 'store/state/domainData/selectors/userPaymentTransaction';
import { SET_DOMAIN_DATA } from 'store/state/domainData/types';
import { resetDomainData } from 'store/state/domainData/actions';
import { mutate, resetMutationResponse } from 'store/state/mutationsResponse/actions';
import { IMutationResponse, editListingResponseSelector, initHostedFieldsPaymentResponseSelector, newListingResponseSelector } from 'store/state/mutationsResponse/selectors';
import { SET_MUTATION_RESPONSE } from 'store/state/mutationsResponse/types';
import { RootAction } from 'store/state';
import { routeSelector } from 'store/state/selectors/router';
import { navigateTo } from 'store/state/router/actions';
import { editListingSilentAction, newListingAction } from 'store/state/uploadBulletin/actions';
import { queryData } from 'store/sagas/apiService';
import { IUserPaymentTransactionResponse, LoadOptions, LoadType, MutationType } from './apiService/types';
import { loadCurrentPromotionPlan, FailedAttemptsStore } from './privateUserMonetizationWatcher';
import { TranzilaPaymentMethod, TranzilaModalState, TranzilaModalType } from 'components/tranzila-hosted-fields/types';
import { Route } from 'config/routes';
import { logAndTrackError } from 'helpers/jstracking';
import { uploadBulletinDraft, wizardCreatedListing } from 'components/bulletin-form/wizard/helpers';
import { makeVariantToEventsString } from './analytics/utils';
import { isBulletinLevelPromotionPlanVariant } from 'utils/privateBulletinPromotionPlan';
import { abTestsStateSelector } from 'store/state/ab-tests/selectors';
import { IABTestContext, TranzilaGPayLastCheck } from 'config/abTests';
import { PaymentMethod } from 'store/state/app';


const FAILED_ATTEMPTS_STORE = new FailedAttemptsStore();

const EMPTY_HOSTED_FIELDS_ERRORS: HostedFieldsErrors = {
  card_holder_id_number: null,
  credit_card_number: null,
  cvv: null,
  expiry: null,
};

export const userTransactionPattern = (action: RootAction) => (
  action.type === SET_DOMAIN_DATA && action.loadType === LoadType.UserPaymentTransaction
);

const userTransactionsPattern = (action: RootAction) => (
  action.type === SET_DOMAIN_DATA && action.loadType === LoadType.UserPaymentTransactions
);

const userInitHostedFieldsPaymentPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE
  && action.mutationType === MutationType.InitHostedFieldsPayment
);

const newListingPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.NewListing
);

const editListingMutationPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.EditListing
);

function* resetHostedFieldsErrors() {
  yield put(setTranzilaHostedFieldsModalErrorsAction(EMPTY_HOSTED_FIELDS_ERRORS));
}

function* clearHostedFieldsModalStateAfterFailDismissed(isUploadFlow: boolean) {
  const actionType = isUploadFlow ? SET_HOSTED_FIELDS_FAIL_UPLOAD_MODAL : SET_HOSTED_FIELDS_FAIL_MODAL;
  // Keep modal state alive until the user dismisses the fail modal so that hostedFieldsBulletinIdSelector stays valid for the retry flow
  yield take((a: RootAction) => a.type === actionType && a.payload === false);
  yield put(setHostedFieldsModalAction(null));
}

function* closePromotionPlanModals() {
  yield put(setPrivateBulletinSelectPromotionPlanModal({ isOpen: false, bulletinId: null }));
  yield put(setPrivateBulletinMinimumPromotionPlanModal({ isOpen: false, bulletin: null }));
}

function* navigateToManageBulletinsIfNeeded() {
  const route: ReturnType<typeof routeSelector> = yield select(routeSelector);
  if (route.name !== Route.ManageBulletins) {
    yield put(navigateTo(Route.ManageBulletins));
  }
}

function isUploadBulletinFlow(modalState: TranzilaModalState) {
  return Boolean(modalState && modalState.isUploadFlow);
}

function* transactionsQuery() {
  const loadTransactionOpts: LoadOptions<LoadType.UserPaymentTransactions> = {
    loadType: LoadType.UserPaymentTransactions,
    meta: {
      variables: {
        timestamp: Date.now(),
      },
    },
  };

  yield fork(queryData, loadTransactionOpts);
  yield take(userTransactionsPattern);

  const userTransactions: IUserPaymentTransactionResponse[] = yield select(userPaymentTransactionsDataSelector);
  const pendingTransactions = (userTransactions || []).filter((transaction) => transaction.status === 'PENDING');

  return Boolean(pendingTransactions && pendingTransactions.length);
}

function* handleHostedFieldEvent(event: HostedFieldsEvent) {
  if (event.type === 'googleIsEnable') {
    yield put(setHostedFieldsGoogleEnableAction(true));
    return;
  }

  switch (event.field) {
    case 'credit_card_number':
      yield put(setTranzilaHostedFieldsModalErrorsAction({
        credit_card_number: !event.isValid,
      }));
      break;

    case 'cvv':
      yield put(setTranzilaHostedFieldsModalErrorsAction({
        cvv: !event.isValid,
      }));
      break;

    case 'expiry':
      yield put(setTranzilaHostedFieldsModalErrorsAction({
        expiry: !event.isValid,
      }));
      break;

    case 'card_holder_id_number':
      yield put(setTranzilaHostedFieldsModalErrorsAction({
        card_holder_id_number: !event.isValid,
      }));
      break;

    default:
      break;
  }
}

function* checkGooglePayMode() {
  const abTestsContext: IABTestContext = yield select(abTestsStateSelector);
  return abTestsContext.tranzilaGPayLastCheck === TranzilaGPayLastCheck.modeB;
}

function* tranzilaHostedFieldsModalWatcher(action: ReturnType<typeof setHostedFieldsModalAction>) {
  const isGPayMode = yield call(checkGooglePayMode);

  const payload = action.payload;

  if (!payload) {
    yield put(resetDomainData({ loadType: LoadType.UserPaymentTransactions }));
    return;
  }

  if (payload.modalType === TranzilaModalType.Loading) {
    const hasPendingTransactions = yield call(transactionsQuery);

    yield delay(1000);

    yield put(setHostedFieldsModalAction({
      ...payload,
      modalType: hasPendingTransactions
        ? TranzilaModalType.Processing
        : (isGPayMode ? TranzilaModalType.MethodInitiation : TranzilaModalType.Initiation),
    }));

    return;
  }

  if (payload.modalType === TranzilaModalType.Processing) {
    while (true) {
      const hasPendingTransactions = yield call(transactionsQuery);

      // Small delay to prevent modals from flashing too quickly
      yield delay(1000);

      if (!hasPendingTransactions) {
        yield put(setHostedFieldsModalAction(null));
        yield call(loadCurrentPromotionPlan);
        break;
      }
    }

    return;
  }

  if (isGPayMode) {
    if (payload.modalType === TranzilaModalType.CardInitiation || payload.modalType === TranzilaModalType.MethodInitiation) {
      const isCardStep = payload.modalType === TranzilaModalType.CardInitiation;
      const profile = yield select(userProfileSelector);
      const firstName = profile.firstName;
      const lastName = profile.lastName;
      const phone = profile.phone;
      const contactName = ''.concat(firstName).concat(lastName ? ' ' + lastName : '');

      const modalState: TranzilaModalState | null = yield select(hostedFieldsModalStateSelector);
      let bulletinsIds: string[] | undefined = modalState && modalState.bulletinId ? [ modalState.bulletinId ] : undefined;

      if (isUploadBulletinFlow(modalState) && bulletinsIds) {
        yield put(editListingSilentAction({ listing: { ...modalState.listing, id: bulletinsIds[0] } }));
        yield take(editListingMutationPattern);

        const editRes: IMutationResponse<MutationType.EditListing> = yield select(editListingResponseSelector);
        const editSuccess = editRes && editRes.data && editRes.data.editListing && editRes.data.editListing.listing;

        if (!editSuccess) {
          yield put(setToastMessage({ term: 'bulletinForm.wizard.errorToast', type: 'error' }));
          logAndTrackError('HOSTED FIELDS: Failed to edit existing listing before payment');
          yield put(setHostedFieldsModalAction(null));
          return;
        }
      }
      else if (isUploadBulletinFlow(modalState) && isBulletinLevelPromotionPlanVariant(payload.planInfo) && !bulletinsIds) {
        yield put(newListingAction({ listing: modalState.listing, source: 'wizardPaidPlanViaHostedFields' }));
        yield take(newListingPattern);

        const newListingResponse: IMutationResponse<MutationType.NewListing> = yield select(newListingResponseSelector);
        const bulletinId = newListingResponse && newListingResponse.data && newListingResponse.data.newListing && newListingResponse.data.newListing.listing && newListingResponse.data.newListing.listing.id;
        yield put(resetMutationResponse({ mutationType: MutationType.NewListing, key: null }));

        if (!bulletinId) {
          yield put(setToastMessage({ term: 'error.promotionPlan.requiresBulletinId', type: 'error' }));
          logAndTrackError('HOSTED FIELDS: bulletinId is required');
          yield put(setHostedFieldsModalAction(null));
          return;
        }

        bulletinsIds = [ bulletinId ];
      }

      if (!isBulletinLevelPromotionPlanVariant(payload.planInfo)) {
        bulletinsIds = undefined;
      }

      yield put(mutate({
        mutationType: MutationType.InitHostedFieldsPayment,
        meta: {
          variables: {
            input: {
              productId: payload.planInfo.id,
              contactName,
              phone,
              bulletinsIds,
              method: isCardStep ? TranzilaPaymentMethod.Card : TranzilaPaymentMethod.Other,
            },
          },
        },
      }));

      yield take(userInitHostedFieldsPaymentPattern);

      yield put(setHostedFieldsModalAction({
        modalType: isCardStep ? TranzilaModalType.CardInfo : TranzilaModalType.Method,
        planInfo: payload.planInfo,
      }));
    }
  }
  else {
    if (payload.modalType === TranzilaModalType.Initiation) {
      const profile = yield select(userProfileSelector);
      const firstName = profile.firstName;
      const lastName = profile.lastName;
      const phone = profile.phone;
      const contactName = ''.concat(firstName).concat(lastName ? ' ' + lastName : '');

      const modalState: TranzilaModalState | null = yield select(hostedFieldsModalStateSelector);
      let bulletinsIds: string[] | undefined = modalState && modalState.bulletinId ? [ modalState.bulletinId ] : undefined;

      if (isUploadBulletinFlow(modalState) && bulletinsIds) {
        yield put(editListingSilentAction({ listing: { ...modalState.listing, id: bulletinsIds[0] } }));
        yield take(editListingMutationPattern);

        const editRes: IMutationResponse<MutationType.EditListing> = yield select(editListingResponseSelector);
        const editSuccess = editRes && editRes.data && editRes.data.editListing && editRes.data.editListing.listing;

        if (!editSuccess) {
          yield put(setToastMessage({ term: 'bulletinForm.wizard.errorToast', type: 'error' }));
          logAndTrackError('HOSTED FIELDS: Failed to edit existing listing before payment');
          yield put(setHostedFieldsModalAction(null));
          return;
        }
      }
      else if (isUploadBulletinFlow(modalState) && isBulletinLevelPromotionPlanVariant(payload.planInfo) && !bulletinsIds) {
        yield put(newListingAction({ listing: modalState.listing, source: 'wizardPaidPlanViaHostedFields' }));
        yield take(newListingPattern);

        const newListingResponse: IMutationResponse<MutationType.NewListing> = yield select(newListingResponseSelector);
        const bulletinId = newListingResponse && newListingResponse.data && newListingResponse.data.newListing && newListingResponse.data.newListing.listing && newListingResponse.data.newListing.listing.id;
        yield put(resetMutationResponse({ mutationType: MutationType.NewListing, key: null }));

        if (!bulletinId) {
          yield put(setToastMessage({ term: 'error.promotionPlan.requiresBulletinId', type: 'error' }));
          logAndTrackError('HOSTED FIELDS: bulletinId is required');
          yield put(setHostedFieldsModalAction(null));
          return;
        }

        bulletinsIds = [ bulletinId ];
      }

      if (!isBulletinLevelPromotionPlanVariant(payload.planInfo)) {
        bulletinsIds = undefined;
      }

      yield put(mutate({
        mutationType: MutationType.InitHostedFieldsPayment,
        meta: {
          variables: {
            input: {
              productId: payload.planInfo.id,
              contactName,
              phone,
              bulletinsIds,
            },
          },
        },
      }));

      yield take(userInitHostedFieldsPaymentPattern);

      yield put(setHostedFieldsModalAction({
        modalType: TranzilaModalType.CardInfo,
        planInfo: payload.planInfo,
      }));
    }
  }
}

function* handlePaymentSubmitError(input: { paymentId: string }) {
  const { paymentId } = input;
  logAndTrackError(`HOSTED FIELDS: Payment submit flow failed for payment ID ${paymentId}`);

  const modalState: TranzilaModalState = yield select(hostedFieldsModalStateSelector);

  if (isUploadBulletinFlow(modalState)) {
    yield put(setHostedFieldsFailUploadModalAction(true));
    yield put(setPrivateBulletinSelectPromotionPlanModal({ isOpen: false, bulletinId: null }));
    yield call(resetHostedFieldsErrors);
  }
  else {
    yield put(setHostedFieldsFailModalAction(true));
    yield call(navigateToManageBulletinsIfNeeded);
    yield put(setPrivateBulletinSelectPromotionPlanModal({ isOpen: false, bulletinId: null }));
    yield call(resetHostedFieldsErrors);
  }
  yield call(clearHostedFieldsModalStateAfterFailDismissed, isUploadBulletinFlow(modalState));
}

function* handlePaymentSubmittedViaHostedFieldsWatcher(action: ReturnType<typeof submittedPaymentViaHostedFieldsAction>) {
  const { method, paymentId } = action.payload;
  if (method === PaymentMethod.CARD) {
    yield put(setHostedFieldsModalAction({ modalType: TranzilaModalType.Paying }));
  }

  const initPaymentResponse: IMutationResponse<MutationType.InitHostedFieldsPayment> = yield select(initHostedFieldsPaymentResponseSelector);

  try {
    const paymentFn = tranzilaHostedFieldsService.getPaymentFn(initPaymentResponse, method);
    if (paymentFn) {
      yield call(paymentFn);
    }
    else {
      const errorMessage = `Provided invalid payment method: ${method}. Payment will not be submitted.`;
      logAndTrackError(errorMessage);
      throw new Error(errorMessage);
    }
  }
  catch (error) {
    logAndTrackError('HOSTED FIELDS: Failed to handle payment submitted via hosted fields', error);
    if (method === PaymentMethod.CARD) {
      yield call(handlePaymentSubmitError, { paymentId });
    }
    return;
  }

  yield fork(queryData, {
    loadType: LoadType.UserPaymentTransaction,
    meta: {
      variables: {
        paymentId,
        timestamp: Date.now(),
      },
    },
  });
}

function* userPendingPaymentTransactionStatus(
  action: ReturnType<typeof pendingTranzilaHostedFieldsModalAction>
) {
  yield put(setHostedFieldsModalAction({ modalType: TranzilaModalType.Paying }));

  yield fork(queryData, {
    loadType: LoadType.UserPaymentTransaction,
    meta: {
      variables: {
        paymentId: action.payload.paymentId,
        timestamp: Date.now(),
      },
    },
  });
}

function* userTransactionStatusWatcher(action: any) {
  yield put(setTranzilaHostedFieldsModalErrorsAction(EMPTY_HOSTED_FIELDS_ERRORS));

  const analytics = yield getContext('analytics');
  const sendEvent = analytics.sendEvent;
  const source = yield select(privateUserMonetizationEventsSourceSelector);
  const paymentData: IUserPaymentTransactionResponse = yield select(userPaymentTransactionDataSelector);
  const modalState: TranzilaModalState = yield select(hostedFieldsModalStateSelector);

  const paymentId =
    action
    && action.meta
    && action.meta.variables
    && action.meta.variables.paymentId
      ? action.meta.variables.paymentId
      : undefined;

  if (!paymentData || paymentData.status === 'FAILED') {
    logAndTrackError('HOSTED FIELDS: Payment transaction finished with FAILED status', { paymentId, paymentData });
    if (modalState && modalState.planInfo) {
      FAILED_ATTEMPTS_STORE.increment();
      yield call(sendEvent, 'user_promotion_plan_submit_failed', 'user', {
        event: {
          deal_type: modalState.paymentDealType,
          source,
          promotion_plan: makeVariantToEventsString(modalState.planInfo.variant),
          transaction_id: paymentId,
          transaction_error_code: paymentData.transactionResponseCode,
          dunning_attempt_number: Math.min(FAILED_ATTEMPTS_STORE.get(), 4),
        },
      });
    }

    if (isUploadBulletinFlow(modalState)) {
      yield put(setHostedFieldsFailUploadModalAction(true));
      yield call(closePromotionPlanModals);
    }
    else {
      yield put(setHostedFieldsFailModalAction(true));
      yield call(closePromotionPlanModals);
      yield call(navigateToManageBulletinsIfNeeded);
    }
    yield call(clearHostedFieldsModalStateAfterFailDismissed, isUploadBulletinFlow(modalState));
  }

  if (paymentData && paymentData.status === 'SUCCESS') {
    if (modalState && modalState.planInfo) {
      yield call(sendEvent, 'user_promotion_plan_submit_success', 'user', {
        event: {
          deal_type: modalState.paymentDealType,
          source,
          promotion_plan: makeVariantToEventsString(modalState.planInfo.variant),
          transaction_id: paymentId,
          dunning_attempt_number: Math.min(FAILED_ATTEMPTS_STORE.get(), 4),
          amount_ils: modalState.planInfo.price,
        },
      });
      FAILED_ATTEMPTS_STORE.reset();
    }

    if (isUploadBulletinFlow(modalState)) {
      yield put(setHostedFieldsSuccessUploadModalAction(true));
      yield call(navigateToManageBulletinsIfNeeded);
      yield put(setHostedFieldsModalAction(null));
      yield call(loadCurrentPromotionPlan);
      uploadBulletinDraft.clear();
      wizardCreatedListing.clear();
    }
    else {
      yield call(closePromotionPlanModals);
      yield put(setHostedFieldsSuccessModalAction(true));
      yield call(navigateToManageBulletinsIfNeeded);
      yield put(setHostedFieldsModalAction(null));
      yield call(loadCurrentPromotionPlan);
    }
    return;
  }

  if (paymentData && paymentData.status === 'PENDING') {
    yield put(setHostedFieldsModalAction({ modalType: TranzilaModalType.Paying }));
    // Small delay to prevent modals from flashing too quickly
    yield delay(1000);
    yield put(pendingTranzilaHostedFieldsModalAction({ paymentId }));
    return;
  }
}

function* tranzilaChannelWatcher() {
  const isGPayMode = yield call(checkGooglePayMode);
  while (true) {
    const action: ReturnType<typeof setHostedFieldsModalAction> = yield take(SET_HOSTED_FIELDS_MODAL_STATE);

    if (isGPayMode) {
      if (!action || !action.payload || !(action.payload.modalType === TranzilaModalType.CardInfo || action.payload.modalType === TranzilaModalType.Method)) {
        continue;
      }
    }
    else {
      if (!action || !action.payload || action.payload.modalType !== TranzilaModalType.CardInfo) {
        continue;
      }
    }

    const handshakeData: IMutationResponse<MutationType.InitHostedFieldsPayment> = yield select(
      initHostedFieldsPaymentResponseSelector
    );

    const initPayload =
      handshakeData
      && handshakeData.data
      && handshakeData.data.initHostedFieldsPayment
        ? handshakeData.data.initHostedFieldsPayment
        : null;

    const task = yield fork(function* initHostedFieldsTask() {
      try {
        yield call([ tranzilaHostedFieldsService, tranzilaHostedFieldsService.init ], initPayload, isGPayMode && action.payload.modalType === TranzilaModalType.Method);

        const channel = yield call(
          [ tranzilaHostedFieldsService, tranzilaHostedFieldsService.createChannel ]
        );

        try {
          while (true) {
            const event: HostedFieldsEvent = yield take(channel);
            yield call(handleHostedFieldEvent, event);
          }
        }
        finally {
          channel.close();
        }
      }
      catch (error) {
        logAndTrackError('HOSTED FIELDS: Failed to init hosted fields service', error);

        yield put(setHostedFieldsFailModalAction(true));
      }
    });

    const nextAction: ReturnType<typeof setHostedFieldsModalAction> = yield take(SET_HOSTED_FIELDS_MODAL_STATE);

    const shouldDestroyTranzila = (
      !nextAction
      || !nextAction.payload
      || nextAction.payload === null
      || (
        nextAction.payload.modalType !== TranzilaModalType.CardInfo
        && nextAction.payload.modalType !== TranzilaModalType.Paying
      )
      || (
        isGPayMode
        && nextAction.payload.modalType !== TranzilaModalType.CardInfo
        && nextAction.payload.modalType !== TranzilaModalType.Paying
        && nextAction.payload.modalType !== TranzilaModalType.Method
      )
    );

    if (!shouldDestroyTranzila) {
      continue;
    }

    yield put(resetDomainData({ loadType: LoadType.UserPaymentTransaction }));
    yield put(resetDomainData({ loadType: LoadType.UserPaymentTransactions }));
    yield put(resetMutationResponse({
      mutationType: MutationType.InitHostedFieldsPayment,
      key: null,
    }));
    yield call(resetHostedFieldsErrors);

    yield cancel(task);
    yield call([ tranzilaHostedFieldsService, tranzilaHostedFieldsService.destroy ]);
  }
}

export function* tranzilaHostedFieldsWatcher() {
  yield takeLatest(SET_HOSTED_FIELDS_MODAL_STATE, tranzilaHostedFieldsModalWatcher);
  yield takeLatest(SUBMITTED_PAYMENT_VIA_HOSTED_FIELDS, handlePaymentSubmittedViaHostedFieldsWatcher);
  yield takeEvery(TRANZILA_HOSTED_FIELDS_PENDING_PAYMENT, userPendingPaymentTransactionStatus);
  yield takeLatest(userTransactionPattern, userTransactionStatusWatcher);
  yield fork(tranzilaChannelWatcher);
}
